import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Toast, ToastContainer } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { useGlobal } from "../../GlobalContext";
import { canCreate, canDelete, canEdit } from "../common/permissionUtils";
import DeleteModal from "../common/delete-modal.js";
import Loader from "../common/Loader.jsx";
import { CUSTOM_MODULES_CHANGED, getCustomModulesApi, MODULE_BUILDER, saveCustomModuleApi } from "./custom-module-api";
import { defaultsOf, firstErrors } from "./custom-module-utils";
import { draftFromModule, draftFromTemplate, previewModule, toPayload } from "./builder-draft";
import { FieldsTab, Label } from "./builder-fields";
import { ButtonsTab, DetailViewTab, ListViewTab } from "./builder-views";
import { PreviewTab } from "./builder-templates";

import Plus from "../../assets/svg/add.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const COLUMNS = ["Module", "Menu URL", "Fields", "Buttons"];
const TABS = ["General", "Form Layout", "List View", "Detail View", "Buttons", "Preview"];
// Which tab shows which server error keys, so a failed save opens the right one.
const TAB_ERRORS = {
  General: (key) => key === "name" || key.startsWith("settings"),
  "Form Layout": (key) => key.startsWith("fields"),
  Buttons: (key) => key.startsWith("actions"),
};
const errorStyle = { color: "#ff4444" };

const ModuleBuilder = () => {
  const navigate = useNavigate();
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, MODULE_BUILDER);
  const hasEditPermission = canEdit(rolePermissionList, MODULE_BUILDER);
  const hasDeletePermission = canDelete(rolePermissionList, MODULE_BUILDER);

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null); // the module being edited, while the editor is open
  const [tab, setTab] = useState(TABS[0]);
  const [previewValues, setPreviewValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  const showToast = (message, success) => setToast({ show: true, message, success });

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCustomModulesApi();
      if (response?.data?.success) setModules(response.data.modules ?? []);
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const modulesChanged = () => {
    window.dispatchEvent(new Event(CUSTOM_MODULES_CHANGED));
    fetchModules();
  };

  const openEditor = (module, options) => {
    setErrors({});
    setTab(TABS[0]);
    setDraft(draftFromModule(module, options));
  };

  const openTab = (name) => {
    if (name === "Preview") setPreviewValues(defaultsOf(previewModule(draft)));
    setTab(name);
  };

  const setSetting = (patch) => setDraft((current) => ({ ...current, settings: { ...current.settings, ...patch } }));

  // Replaces the whole draft with a template's setup, filling every tab.
  const applyTemplate = (template) => {
    const started = draft.name.trim() || draft.fields.some((field) => field.label.trim());
    if (started && !window.confirm(`Replace what you have set up so far with the "${template.name}" template?`)) return false;
    const next = draftFromTemplate(template, modules.map((module) => module.name));
    setDraft(next);
    setErrors({});
    setPreviewValues(defaultsOf(previewModule(next)));
    showToast(`"${template.name}" applied: every tab is filled in. Review it and save.`, true);
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      const response = await saveCustomModuleApi(draft.id, toPayload(draft));

      if (response?.status === 400) {
        const found = firstErrors(response.data?.error);
        setErrors(found);
        const errorTab = TABS.find((name) => TAB_ERRORS[name] && Object.keys(found).some(TAB_ERRORS[name]));
        if (errorTab) setTab(errorTab);
        showToast(response.data?.message ?? "Validation error", false);
        return;
      }
      showToast(response?.data?.message ?? "Saved", response?.data?.success);
      if (response?.data?.success) {
        setDraft(null);
        modulesChanged();
      }
    } catch (error) {
      console.error("Error saving module:", error);
      showToast("Unable to save module", false);
    } finally {
      setSaving(false);
    }
  };

  const toastNode = (
    <ToastContainer position="top-end">
      <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
        <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
      </Toast>
    </ToastContainer>
  );

  if (draft) {
    const { settings } = draft;
    const errorMessages = [...new Set(Object.values(errors))];

    const general = (
      <div className="p-4 mb-3 cm-card">
        {!draft.id && !draft.cloneOf && !draft.templateOf && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-3 mb-3" style={{ background: "#F7F8FF", borderRadius: "12px" }}>
            <span className="fs-13 fw-500 text-dkblue">Want a head start? Pick a ready-made design (vendors, assets, tickets, expenses, projects…).</span>
            <button type="button" className="fs-13 fw-600 cm-pill" onClick={() => openTab("Preview")}>Browse templates</button>
          </div>
        )}
        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="mb-name">MODULE NAME *</Label>
            <input
              id="mb-name"
              className="form-control"
              maxLength={100}
              value={draft.name}
              onChange={(e) => setDraft((current) => ({ ...current, name: e.target.value }))}
              placeholder="Ex: Vendors"
            />
            <div className="fs-12 fw-500 text-grey mt-1">Shown in the sidebar and as the list page title.</div>
            {errors.name && <div className="fs-12" style={errorStyle}>{errors.name}</div>}
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="mb-singular">SINGULAR NAME</Label>
            <input
              id="mb-singular"
              className="form-control"
              maxLength={100}
              value={settings.singular}
              onChange={(e) => setSetting({ singular: e.target.value })}
              placeholder="Ex: Vendor"
            />
            <div className="fs-12 fw-500 text-grey mt-1">Used on buttons and titles, Ex: "Add Vendor".</div>
            {errors["settings.singular"] && <div className="fs-12" style={errorStyle}>{errors["settings.singular"]}</div>}
          </Col>
          <Col md={12} className="mb-3">
            <Label htmlFor="mb-description">DESCRIPTION</Label>
            <textarea
              id="mb-description"
              className="form-control"
              rows={2}
              maxLength={500}
              value={settings.description}
              onChange={(e) => setSetting({ description: e.target.value })}
              placeholder="Shown under the list page title"
            />
          </Col>
          <Col md={6}>
            <Label htmlFor="mb-prefix">RECORD NUMBER PREFIX</Label>
            <input
              id="mb-prefix"
              className="form-control"
              maxLength={10}
              value={settings.code_prefix}
              onChange={(e) => setSetting({ code_prefix: e.target.value.toUpperCase() })}
              placeholder="Ex: VEN-"
            />
            <div className="fs-12 fw-500 text-grey mt-1">
              {settings.code_prefix
                ? `Records created from now on are numbered ${settings.code_prefix}00001, ${settings.code_prefix}00002, …`
                : "Leave empty for no record numbers."}
            </div>
            {errors["settings.code_prefix"] && <div className="fs-12" style={errorStyle}>{errors["settings.code_prefix"]}</div>}
          </Col>
        </Row>
      </div>
    );

    const content = {
      General: general,
      "Form Layout": <FieldsTab draft={draft} setDraft={setDraft} modules={modules} errors={errors} />,
      "List View": <ListViewTab draft={draft} setDraft={setDraft} />,
      "Detail View": <DetailViewTab draft={draft} setDraft={setDraft} />,
      Buttons: <ButtonsTab draft={draft} setDraft={setDraft} errors={errors} />,
      Preview: <PreviewTab draft={draft} values={previewValues} setValues={setPreviewValues} onUseTemplate={applyTemplate} />,
    };

    return (
      <div style={{ padding: "16px" }}>
        {toastNode}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <div className="fs-20 fw-600 text-dkblue">
              {draft.id ? `Edit Module: ${draft.name}` : draft.cloneOf ? "Clone Module" : "Create Module"}
            </div>
            {draft.cloneOf && <div className="fs-13 fw-500 text-grey">Copy of {draft.cloneOf}: fields, layout, views and buttons. Records are not copied.</div>}
            {draft.templateOf && <div className="fs-13 fw-500 text-grey">Started from the {draft.templateOf} template. Change anything before saving.</div>}
          </div>
          <div className="d-flex gap-2">
            <button className="btn-cancel" onClick={() => setDraft(null)}>Back</button>
            <button className="btn-save" disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save Module"}
            </button>
          </div>
        </div>

        {errorMessages.length > 0 && (
          <div className="p-3 mb-3" role="alert" style={{ background: "#FDF0F0", borderRadius: "12px", color: "#D61916" }}>
            <div className="fs-14 fw-600 mb-1">Fix these before saving:</div>
            {errorMessages.map((message) => (
              <div key={message} className="fs-13 fw-500">{message}</div>
            ))}
          </div>
        )}

        <div className="tabs-container mb-3">
          <div className="tab-list" role="tablist" style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
            {TABS.map((name) => {
              const hasError = TAB_ERRORS[name] && Object.keys(errors).some(TAB_ERRORS[name]);
              return (
                <button key={name} type="button" role="tab" aria-selected={tab === name} className={`tab-btn ${tab === name ? "active" : ""}`} onClick={() => openTab(name)}>
                  {name}
                  {hasError && <span style={{ color: "#D61916" }}> •</span>}
                </button>
              );
            })}
          </div>
        </div>

        {content[tab]}
      </div>
    );
  }

  const gridColumns = `repeat(${COLUMNS.length}, minmax(0, 1fr)) 170px`;

  return (
    <div style={{ padding: "16px" }}>
      {toastNode}
      <Row className="align-items-center mb-3">
        <Col sm={12} md={6}>
          <div className="fs-20 fw-600 text-dkblue">Module Builder</div>
          <div className="fs-13 fw-500 text-grey">{modules.length} modules</div>
        </Col>
        <Col sm={12} md={6} className="d-flex justify-content-end">
          {/* {hasCreatePermission && ( */}
            <div
              className="d-flex align-items-center gap-2"
              style={{ background: "#3D4AF5", borderRadius: "8px", height: "40px", padding: "0 16px", cursor: "pointer" }}
              onClick={() => openEditor(null)}
            >
              <img src={Plus} alt="" style={{ width: "14px" }} />
              <div className="fs-15 fw-600 text-white">Add Module</div>
            </div>
          {/* )} */}
        </Col>
      </Row>

      <div style={{ borderRadius: "16px", border: "1px solid #ebebeb", backgroundColor: "#ffffff", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: gridColumns, borderBottom: "1px solid #ebebeb" }}>
          {[...COLUMNS, "Action"].map((label) => (
            <div key={label} className="fs-12 fw-600 text-ltgrey" style={{ padding: "12px 16px" }}>
              {label.toUpperCase()}
            </div>
          ))}
        </div>

        {loading ? (
          <Loader height="160px" />
        ) : modules.length === 0 ? (
          <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "40px 0" }}>
            No modules yet. Click Add Module to build one.
          </div>
        ) : (
          modules.map((module) => (
            <div key={module.id} style={{ display: "grid", gridTemplateColumns: gridColumns, borderBottom: "1px solid #f3f3f3", alignItems: "center" }}>
              <div className="fs-14 fw-600 textblue" style={{ padding: "14px 16px", cursor: "pointer" }} onClick={() => navigate(`/custom/${module.slug}/list`)}>
                {module.name}
              </div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>/custom/{module.slug}/list</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{module.fields.length}</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{module.actions?.length ?? 0}</div>
              <div className="d-flex align-items-center gap-3" style={{ padding: "14px 16px" }}>
                {hasEditPermission && <EditRow style={{ cursor: "pointer" }} role="button" aria-label={`Edit ${module.name}`} onClick={() => openEditor(module)} />}
                {/* Cloning creates a module, so it follows the Add Module button. */}
                <button type="button" className="fs-13 fw-600 cm-pill" onClick={() => openEditor(module, { clone: true })}>
                  Clone
                </button>
                {hasDeletePermission && <DeleteRow style={{ cursor: "pointer" }} role="button" aria-label={`Delete ${module.name}`} onClick={() => setDeleteId(module.id)} />}
              </div>
            </div>
          ))
        )}
      </div>

      {deleteId && (
        <DeleteModal
          deleteUrl={`custom_module/${deleteId}`}
          onSuccess={(result) => {
            setDeleteId(null);
            showToast(result?.message ?? "Deleted", result?.success);
            modulesChanged();
          }}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};

export default ModuleBuilder;
